export const Enum_Currency = Object.freeze({
  CNY: '人民币',
  USD: '美元',
  USDTERC: 'USDTERC',
  USDT: 'USDT'
})

export const getDisplayCurrencyText = (currency) => {
  const text = Enum_Currency[currency]
  return text || currency
}

export const getDevice = () => {
  // const isIOS = !!navigator.platform.match(/ipad|iphone|ipod|mac/gi);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
  const isMobile = !!navigator.userAgent.match(/mobile|iphone|ipad|android/gi)
  return { isIOS, isMobile }
}

export const errorMap = Object.freeze({
  appInfo: '000',
  getFaceChannel: '001',
  getFaceStatus: '002',
  goDeposit: '003',
  goWithdrawal: '004',
  getAuthInfo: '005',
  getCacheCustomerByToken: '006'
})

export const checkEnableMT5 = (queryParam) => {
  const includedFlavors = process.env.VUE_APP_MT5_INCLUDED_FLAVORS.split(',').map(flavor => flavor.toUpperCase())
  if (
    getDevice().isIOS &&
    queryParam.flavor &&
    !includedFlavors.includes(queryParam.flavor.toUpperCase())
  ) {
    localStorage.setItem('enableMT5', 'false')
  }
}

export const isMT5Enabled = () => {
  return JSON.parse(localStorage.getItem('enableMT5'))
}

/**
 * Attempts to format a string into a valid URL that can be used with `new URL()`.
 * This function tries to make intelligent guesses for common malformed URL patterns,
 * prioritizing `https://` for assumed web domains.
 *
 * @param {string} input The string to format into a URL.
 * @param {string} [baseUrl] An optional base URL to resolve relative URLs against.
 * If not provided, `window.location.origin` is used if available.
 * @returns {string|null} A formatted URL string, or null if a valid URL cannot be formed.
 */
const formatValidUrl = (input, baseUrl) => {
  if (typeof input !== 'string' || input.trim() === '') {
    return null
  }

  const trimmedInput = input.trim()

  // 1. Try with the input as is (handles already valid URLs with any scheme)
  try {
    new URL(trimmedInput)
    return trimmedInput
  } catch (e) {
    // Not a valid URL as is, continue trying
  }

  // Determine the base URL to use for relative URLs
  let effectiveBaseUrl = baseUrl
  if (!effectiveBaseUrl) {
    if (typeof window !== 'undefined' && window.location && window.location.origin) {
      effectiveBaseUrl = window.location.origin
    }
    // If no base URL is provided and we're not in a browser environment,
    // we won't be able to resolve relative paths reliably later without a base.
  }

  // 2. Add 'https://' or 'http://' if it looks like a domain or an IP address without a scheme,
  //    and doesn't start with common relative path indicators.
  const schemeRegex = /^(https?|ftp|file|mailto|data|blob):/i
  // A more comprehensive list of common TLDs for better guesswork
  const commonTopLevelDomains = /\.(com|org|net|gov|edu|io|co|uk|jp|cn|de|fr|au|ca|br|in|pk|lk|ph|sg|kr|my|mx|za|se|ch|ru|ar|cl|es|it|nl|dk|no|fi|ie|pt|gr|be|ae|sa|qa|kw|bh|eg|ma|dz|tn|tr|th|id|vn|hk|tw|ng|ke|tz|ug|gh|et|rw|zm|zw|app|dev|blog|shop|online|xyz|info|biz|club|space|site|tech|website|art|store|wiki|press|me|name|us|eu|asia|africa)$/i

  if (!schemeRegex.test(trimmedInput) &&
        !trimmedInput.startsWith('/') &&
        !trimmedInput.startsWith('./') &&
        !trimmedInput.startsWith('../') &&
        !trimmedInput.startsWith('#') && // Anchor links
        !trimmedInput.startsWith('?') // Query string only
  ) {
    // Check if it looks like a domain (contains a dot and/or a common TLD) or an IP address
    if ((trimmedInput.includes('.') && commonTopLevelDomains.test(trimmedInput)) ||
            /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}(:\d+)?$/.test(trimmedInput) || // IP address
            trimmedInput.toLowerCase().startsWith('localhost') // localhost
    ) {
      // Prioritize HTTPS
      try {
        const urlWithHttps = 'https://' + trimmedInput
        new URL(urlWithHttps) // This will throw if it's not a valid URL even with https://
        return urlWithHttps
      } catch (e) {
        // If HTTPS fails, try HTTP
        try {
          const urlWithHttp = 'http://' + trimmedInput
          new URL(urlWithHttp)
          return urlWithHttp
        } catch (e2) {
          // Both HTTPS and HTTP failed, continue to next strategy
        }
      }
    }
  }

  // 3. Try resolving as a relative URL if a baseUrl is available
  if (effectiveBaseUrl) {
    try {
      const resolvedUrl = new URL(trimmedInput, effectiveBaseUrl)
      // Ensure the resolved URL is absolute and not just a relative path against the base itself
      // (e.g., if input was '#' and base was 'http://example.com/page', new URL would produce 'http://example.com/page#')
      if (resolvedUrl.protocol !== 'about:') { // Exclude cases like new URL('', 'base') which might yield 'about:blank'
        return resolvedUrl.toString()
      }
    } catch (e) {
      // Still not valid, continue
    }
  }

  // If all attempts fail, return null
  return null
}

export const removeQueryParam = (url, paramKey) => {
  const urlObj = new URL(formatValidUrl(url))
  const searchParams = urlObj.searchParams
  searchParams.delete(paramKey)
  urlObj.search = searchParams.toString()
  return urlObj.toString()
}

export const maskBankAccountNumber = (input) => {
  // Convert input to string to ensure methods work
  const str = String(input)
  const length = str.length

  // CONDITION 1: Numbers Only
  // Check if string contains only digits 0-9
  if (/^\d+$/.test(str)) {
    // Slice returns the last 4 characters.
    // If the number is shorter than 4 digits, it returns the whole number.
    return '**** ' + str.slice(-4)
  }

  // CONDITION 2: Short Strings (6 characters or less)
  if (length <= 6) {
    // If string is too short to split (empty or 1 char), return as is or handle specifically
    if (length < 2) return str

    // Keep 1st char + *** + Last char
    return str[0] + ' *** ' + str[length - 1]
  }

  // CONDITION 3: Standard Strings (More than 6 characters)
  // Keep first 3 + *** + Last 3
  return str.slice(0, 3) + ' *** ' + str.slice(-3)
}

/**
 * Returns the corresponding domain environment variable based on the customer number prefix.
 * * @param {string|number} customerNumber - The customer number to evaluate.
 * @returns {string|null} The domain from the environment variables, or process.env.VUE_APP_AC if no match.
 */
export function getAcDomainByCustomerNumber(customerNumber) {
  const prefix = String(customerNumber).substring(0, 3);
  
  const domainMap = {
    '853': process.env.VUE_APP_OSZHAC,
    '851': process.env.VUE_APP_OSGJAC,
    '831': process.env.VUE_APP_DJAC,
    '833': process.env.VUE_APP_HTZAC,
  };

  // Return the matched domain, or fallback to the default VUE_APP_AC
  return domainMap[prefix] || process.env.VUE_APP_AC;
}